import React, { ReactNode } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { CheckCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface ChallengeItem {
  icon: ReactNode;
  title: string;
  description: string;
  solution: string;
}

export interface ChallengeBlockProps {
  title: string;
  subtitle?: string;
  challenges: ChallengeItem[];
  variant?: 'orange' | 'blue';
  id?: string;
  className?: string;
  titleClassName?: string;
  subtitleClassName?: string;
  gridClassName?: string;
}

const ChallengeBlock: React.FC<ChallengeBlockProps> = ({
  title,
  subtitle,
  challenges,
  variant = 'orange',
  id,
  className,
  titleClassName,
  subtitleClassName,
  gridClassName
}) => {
  // Farbvarianten basierend auf dem gewählten Farbschema
  const colorVariants = {
    orange: {
      iconBg: 'bg-orange-50',
      iconText: 'text-orange-500',
      solutionBg: 'bg-blue-50',
      solutionIcon: 'text-blue-600',
      solutionText: 'text-blue-700'
    },
    blue: {
      iconBg: 'bg-blue-50',
      iconText: 'text-blue-600',
      solutionBg: 'bg-orange-50',
      solutionIcon: 'text-orange-600',
      solutionText: 'text-orange-700'
    }
  };

  const colors = colorVariants[variant];

  return (
    <section id={id} className={cn("py-16 bg-gray-50", className)}>
      <div className="container px-4 mx-auto">
        <h2 className={cn("text-3xl md:text-4xl font-bold text-center mb-2", titleClassName)}>
          {title}
        </h2>
        
        {subtitle && (
          <p className={cn("text-xl text-gray-600 text-center max-w-3xl mx-auto mb-16", subtitleClassName)}>
            {subtitle}
          </p>
        )}
        
        <div className={cn("grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6", gridClassName)}>
          {challenges.map((challenge, index) => (
            <Card 
              key={index}
              className="bg-white shadow-md rounded-lg hover:shadow-xl transition-shadow duration-300"
            >
              <CardContent className="p-6 flex flex-col h-full">
                {/* Icon in eigener Zeile aber linksbündig */}
                <div className="mb-4">
                  <div className={cn("p-3 rounded-full flex items-center justify-center w-16 h-16", colors.iconBg)}>
                    {/* Icon mit der richtigen Farbe */}
                    {React.isValidElement(challenge.icon) 
                      ? React.cloneElement(challenge.icon as React.ReactElement, { 
                          className: cn("h-8 w-8", colors.iconText, 
                            (challenge.icon as React.ReactElement).props.className) 
                        })
                      : challenge.icon}
                  </div>
                </div>
                
                {/* Titel und Beschreibung linksbündig */}
                <div className="mb-4">
                  <h3 className="text-xl font-bold mb-3">{challenge.title}</h3>
                  <p className="text-gray-600">
                    {challenge.description}
                  </p>
                </div>
                
                {/* Lösungs-Statement */}
                <div className={cn("flex items-start mt-auto p-3 rounded-md", colors.solutionBg)}>
                  <CheckCircle className={cn("h-5 w-5 mr-2 mt-0.5 flex-shrink-0", colors.solutionIcon)} />
                  <p className={cn("text-sm font-medium", colors.solutionText)}>
                    {challenge.solution}
                  </p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ChallengeBlock; 